import React, { useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material'
import {
  Home as HomeIcon,
  Storage as StorageIcon,
  Key as KeyIcon,
  People as PeopleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material'
import FlashMessages from '@/components/shared/FlashMessages'
import { FlexRow, MainContent, sidebarStyles } from '@/components/styled'
import { SIDEBAR_WIDTH, SPACING } from '@/theme'
import type { Account, User, Flash } from '@/types'

interface ManagementLayoutProps {
  children: React.ReactNode
}

interface NavItem {
  name: string
  path: string
  icon: React.ElementType
}

export default function ManagementLayout({ children }: ManagementLayoutProps) {
  const { auth, currentAccount, isAccountAdmin, userAccounts, flash } = usePage<{
    auth: { user: User | null }
    currentAccount?: Account
    isAccountAdmin?: boolean
    userAccounts?: Account[]
    flash: Flash
  }>().props

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null)

  const navigationItems: NavItem[] = currentAccount
    ? [
        { name: 'Dashboard', path: `/accounts/${currentAccount.slug}/dashboard`, icon: HomeIcon },
        { name: 'Backends', path: `/accounts/${currentAccount.slug}/backends`, icon: StorageIcon },
        { name: 'Credentials', path: `/accounts/${currentAccount.slug}/credentials`, icon: KeyIcon },
        { name: 'Team Members', path: `/accounts/${currentAccount.slug}/members`, icon: PeopleIcon },
      ]
    : []

  const isActive = (path: string) => {
    if (typeof window !== 'undefined') {
      return window.location.pathname.startsWith(path)
    }
    return false
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {currentAccount && (
        <Drawer
          variant="permanent"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              bgcolor: '#1a1a1a',
              color: 'white',
              borderRadius: 0,
            },
          }}
        >
          <Toolbar
            sx={{
              borderBottom: 1,
              borderColor: 'rgba(255, 255, 255, 0.1)',
              minHeight: '64px !important',
            }}
          >
            <Typography
              variant="h6"
              component={Link}
              href="/accounts"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
            >
              S3 Proxy
            </Typography>
          </Toolbar>

          <List sx={{ px: 1, py: 2, flex: 1 }}>
            {navigationItems.map((item) => (
              <ListItemButton
                key={item.name}
                component={Link}
                href={item.path}
                selected={isActive(item.path)}
                sx={sidebarStyles.navItem}
              >
                <ListItemIcon sx={sidebarStyles.navIcon}>
                  <item.icon />
                </ListItemIcon>
                <ListItemText primary={item.name} />
              </ListItemButton>
            ))}
          </List>

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="caption" sx={{ color: 'grey.500', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 500 }}>
              Account
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
              {currentAccount.name}
            </Typography>
          </Box>
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar>
            {!currentAccount && (
              <Typography
                variant="h6"
                component={Link}
                href="/accounts"
                sx={{ textDecoration: 'none', color: 'text.primary', fontWeight: 600 }}
              >
                S3 Proxy
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />

            {userAccounts && userAccounts.length > 1 && (
              <>
                <IconButton
                  onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
                  sx={{
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75,
                    mr: 1,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <Typography variant="body2" sx={{ mr: 0.5 }}>
                    {currentAccount?.name || 'Select Account'}
                  </Typography>
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={accountMenuAnchor}
                  open={Boolean(accountMenuAnchor)}
                  onClose={() => setAccountMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {userAccounts.map((account) => (
                    <MenuItem
                      key={account.id}
                      component={Link}
                      href={`/accounts/${account.slug}/dashboard`}
                      selected={account.id === currentAccount?.id}
                      onClick={() => setAccountMenuAnchor(null)}
                    >
                      {account.name}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem
                    component={Link}
                    href="/accounts/new"
                    onClick={() => setAccountMenuAnchor(null)}
                    sx={{ color: 'primary.main' }}
                  >
                    + Create new account
                  </MenuItem>
                </Menu>
              </>
            )}

            <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ p: 0 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {auth?.user?.email?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important' }}>
                <Typography variant="body2" color="text.secondary">
                  {auth?.user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem component={Link} href="/accounts" onClick={() => setUserMenuAnchor(null)}>
                All Accounts
              </MenuItem>
              <MenuItem
                component={Link}
                href="/users/sign_out"
                method="delete"
                as="button"
                onClick={() => setUserMenuAnchor(null)}
              >
                Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: SPACING.page,
            bgcolor: 'background.default',
          }}
        >
          <MainContent>
            <FlashMessages flash={flash} />
            {children}
          </MainContent>
        </Box>
      </Box>
    </Box>
  )
}
